import React, { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, AlertTriangle, Activity, 
  Calendar, Pill, Home, HeartPulse, ArrowUpRight,
  BarChart3, PieChart, LineChart
} from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

/**
 * Population Health Analytics Dashboard
 * For admins and care managers to view population-level metrics
 */
const PopulationHealthDashboard = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadMetrics();
  }, [timeRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/analytics/population?range=${timeRange}`);
      if (res.data?.data) {
        setMetrics(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to load analytics');
      setError(true);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <h3 className="font-bold text-lg text-slate-900 mb-1">Failed to load analytics</h3>
        <p className="text-sm text-slate-500 mb-4">Could not retrieve population health data.</p>
        <button onClick={() => { setError(false); loadMetrics(); }} className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-all">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Population Health Analytics</h1>
          <p className="text-slate-500">Care at home program metrics and trends</p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex bg-slate-100 rounded-xl p-1">
          {['7d', '30d', '90d'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                timeRange === range 
                  ? 'bg-white text-primary shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {range === '7d' ? 'Last 7 Days' : range === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Users}
          label="Total Patients"
          value={metrics?.totalPatients || 0}
          change={metrics?.patientChange}
          subtitle={metrics?.newPatientsInPeriod ? `+${metrics.newPatientsInPeriod} new this period` : undefined}
          color="blue"
        />
        <MetricCard
          icon={AlertTriangle}
          label="High Risk Patients"
          value={metrics?.highRiskCount || 0}
          change={metrics?.highRiskChange}
          subtitle={`${((metrics?.highRiskCount / metrics?.totalPatients) * 100 || 0).toFixed(1)}% of population`}
          color="red"
        />
        <MetricCard
          icon={Home}
          label="Home Visits Completed"
          value={metrics?.homeVisits || 0}
          change={metrics?.visitChange}
          color="green"
        />
        <MetricCard
          icon={HeartPulse}
          label="Readmissions Prevented"
          value={metrics?.readmissionsPrevented || 0}
          change={metrics?.readmissionChange}
          color="purple"
        />
      </div>

      {/* Risk Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary" />
            Risk Distribution
          </h3>
          <RiskDistributionChart data={metrics?.riskDistribution} />
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Top Risk Factors
          </h3>
          <TopRiskFactors data={metrics?.topRiskFactors} />
        </div>
      </div>

      {/* Trends */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <LineChart className="w-5 h-5 text-primary" />
          Intervention Outcomes
        </h3>
        <InterventionTrends data={metrics?.interventions} />
      </div>

      {/* Breakdown Detection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BreakdownCard
          icon={Pill}
          title="Medication Gaps"
          count={metrics?.medicationGaps || 0}
          trend={metrics?.medicationTrend}
          color="amber"
        />
        <BreakdownCard
          icon={Calendar}
          title="Missed Appointments"
          count={metrics?.missedAppointments || 0}
          trend={metrics?.appointmentTrend}
          color="rose"
        />
        <BreakdownCard
          icon={Activity}
          title="Service Gaps"
          count={metrics?.serviceGaps || 0}
          trend={metrics?.serviceTrend}
          color="red"
        />
      </div>

      {/* Cohort Analysis */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg text-slate-900 mb-4">Cohort Analysis</h3>
        <CohortTable cohorts={metrics?.cohorts} />
      </div>

      {/* Program Impact */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-6 border border-primary/10">
        <h3 className="font-bold text-xl text-slate-900 mb-4">Program Impact Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ImpactMetric
            label="Hospital Days Avoided"
            value={metrics?.hospitalDaysAvoided || 0}
            costSavings={(metrics?.hospitalDaysAvoided || 0) * 2500}
          />
          <ImpactMetric
            label="Emergency Visits Prevented"
            value={metrics?.erVisitsPrevented || 0}
            costSavings={(metrics?.erVisitsPrevented || 0) * 1800}
          />
          <ImpactMetric
            label="Clinician Hours Saved"
            value={metrics?.clinicianHoursSaved || 0}
            costSavings={(metrics?.clinicianHoursSaved || 0) * 150}
          />
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon: Icon, label, value, change, subtitle, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100'
  };

  return (
    <div className={`p-5 rounded-2xl border ${colors[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-white/50 rounded-xl">
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm font-bold text-slate-600">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold text-slate-900">{value.toLocaleString()}</span>
        {change !== undefined && (
          <span className={`text-xs font-bold flex items-center ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            <ArrowUpRight className={`w-3 h-3 ${change < 0 ? 'rotate-90' : ''}`} />
            {Math.abs(change)}%
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
};

// Risk Distribution Chart (Visual Bar)
const RiskDistributionChart = ({ data }) => {
  const distribution = data || {
    critical: 5,
    high: 15,
    medium: 30,
    low: 50
  };

  const total = Object.values(distribution).reduce((a, b) => a + b, 0);

  const colors = {
    critical: 'bg-red-500',
    high: 'bg-amber-500',
    medium: 'bg-blue-500',
    low: 'bg-emerald-500'
  };

  return (
    <div className="space-y-4">
      {Object.entries(distribution).map(([level, count]) => (
        <div key={level} className="flex items-center gap-3">
          <div className="w-20 text-sm font-bold text-slate-600 capitalize">{level}</div>
          <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
            <div
              className={`h-full ${colors[level]} transition-all duration-500`}
              style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }}
            />
          </div>
          <div className="w-12 text-right text-sm font-bold text-slate-900">{count}</div>
        </div>
      ))}
    </div>
  );
};

// Top Risk Factors
const riskFactorLabels = {
  symptom_reported: 'Symptom Reported',
  medication_picked_up: 'Medication Picked Up',
  medication_missed: 'Medication Missed',
  medication_taken: 'Medication Taken',
  device_reading: 'Device Reading',
  doctor_response: 'Doctor Response',
  mood_check_in: 'Mood Check-in',
  mood_checkin: 'Mood Check-in',
  appointment_attended: 'Appointment Attended',
  appointment_missed: 'Appointment Missed',
  service_missed: 'Service Missed',
  fall_detected: 'Fall Detected',
  emergency_pressed: 'Emergency Pressed',
  care_plan_updated: 'Care Plan Updated',
  risk_assessment: 'Risk Assessment',
  patient_distress: 'Patient Distress',
  confused: 'Patient Confused',
  custom: 'Custom Event',
};

const formatFactorName = (name) => {
  if (riskFactorLabels[name]) return riskFactorLabels[name];
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const TopRiskFactors = ({ data }) => {
  const factors = data || [];

  if (factors.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">No risk factor data available for this period.</p>;
  }

  return (
    <div className="space-y-3">
      {factors.map((factor, i) => (
        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center">
              {i + 1}
            </span>
            <span className="font-bold text-slate-900">{formatFactorName(factor.name)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-slate-600">{factor.count} patients</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
              factor.trend?.startsWith('+') ? 'bg-red-100 text-red-600' :
              factor.trend?.startsWith('-') ? 'bg-emerald-100 text-emerald-600' :
              'bg-slate-100 text-slate-600'
            }`}>
              {factor.trend || '0%'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Intervention Trends
const InterventionTrends = ({ data }) => {
  const interventions = data || [];

  if (interventions.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">No intervention data available for this period.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {interventions.map((item, i) => (
        <div key={i} className="p-4 bg-slate-50 rounded-xl">
          <h4 className="font-bold text-slate-900 mb-2">{item.type}</h4>
          <div className="flex items-end gap-2 mb-2">
            <span className="text-2xl font-bold text-primary">{item.completed}</span>
            <span className="text-sm text-slate-500">/ {item.scheduled}</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${item.scheduled > 0 ? Math.min((item.completed / item.scheduled) * 100, 100) : 0}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">{item.success}% completion rate</p>
        </div>
      ))}
    </div>
  );
};

// Breakdown Detection Card
const BreakdownCard = ({ icon: Icon, title, count, trend, color }) => {
  const colors = {
    amber: 'bg-amber-50 border-amber-200',
    rose: 'bg-rose-50 border-rose-200',
    red: 'bg-red-50 border-red-200'
  };

  return (
    <div className={`p-5 rounded-2xl border ${colors[color]}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 bg-white rounded-xl">
          <Icon className="w-5 h-5 text-slate-600" />
        </div>
        {trend && (
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 font-medium">{title}</p>
      <p className="text-2xl font-bold text-slate-900">{count}</p>
      <p className="text-xs text-slate-500 mt-1">This {trend > 0 ? 'increased' : 'decreased'} vs last period</p>
    </div>
  );
};

// Cohort Table
const CohortTable = ({ cohorts }) => {
  const data = cohorts || [];

  if (data.length === 0) {
    return <p className="text-sm text-slate-500 py-6 text-center">No cohort data available for this period.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[400px]">
        <thead>
          <tr className="text-left border-b border-slate-200">
            <th className="pb-3 font-bold text-slate-600 text-sm">Cohort</th>
            <th className="pb-3 font-bold text-slate-600 text-right text-sm whitespace-nowrap">Patients</th>
            <th className="pb-3 font-bold text-slate-600 text-right text-sm whitespace-nowrap">Readmit %</th>
            <th className="pb-3 font-bold text-slate-600 text-right text-sm whitespace-nowrap">Avg Risk</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {data.map((cohort, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-3 font-medium text-slate-900 break-words">{cohort.name}</td>
              <td className="py-3 text-right text-slate-600 whitespace-nowrap">{cohort.count}</td>
              <td className="py-3 text-right">
                <span className={`font-bold ${cohort.readmissionRate > 10 ? 'text-red-600' : 'text-slate-600'}`}>
                  {cohort.readmissionRate}%
                </span>
              </td>
              <td className="py-3 text-right">
                <span className={`font-bold px-2 py-1 rounded-full text-xs ${
                  cohort.avgRisk >= 70 ? 'bg-red-100 text-red-600' :
                  cohort.avgRisk >= 50 ? 'bg-amber-100 text-amber-600' :
                  'bg-emerald-100 text-emerald-600'
                }`}>
                  {cohort.avgRisk}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Impact Metric
const ImpactMetric = ({ label, value, costSavings }) => {
  return (
    <div className="text-center">
      <p className="text-4xl font-bold text-primary mb-1">{value.toLocaleString()}</p>
      <p className="text-sm font-bold text-slate-600 mb-2">{label}</p>
      <p className="text-xs text-emerald-600 font-bold">
        Est. Cost Savings: ${costSavings.toLocaleString()}
      </p>
    </div>
  );
};

export default PopulationHealthDashboard;
